// =============================================================================
// MeuDriver — Hook: useRecurringBookings (Batch S1)
// Agendamento recorrente — criação e cancelamento de séries de aulas
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

// ─── Types ────────────────────────────────────────────────────────────────────

// `type` e não `interface`: só type aliases ganham index signature implícita e
// portanto são atribuíveis a `Record<string, unknown>`, exigido para passar o
// objeto como argumento de RPC (`p_recurrence`).
export type RecurrencePattern = {
  /** Dias da semana: 0=dom, 1=seg, 2=ter, 3=qua, 4=qui, 5=sex, 6=sab */
  days: number[]
  /** Horário no formato "HH:mm" */
  time: string
  /** Número de semanas (1–52) */
  duration_weeks: number
}

export interface BookingSeries {
  id: string
  student_id: string
  instructor_id: string
  recurrence_pattern: RecurrencePattern
  booking_type: string | null
  notes: string | null
  status: 'active' | 'cancelled'
  created_at: string
}

export interface CreateBookingSeriesParams {
  instructor_id: string
  recurrence: RecurrencePattern
  booking_type: string
  notes?: string
}

export interface CreateBookingSeriesResult {
  series_id: string
  bookings_created: number
}

// ─── Query keys ───────────────────────────────────────────────────────────────

const seriesKeys = {
  all:    ['booking-series'] as const,
  detail: (id: string) => ['booking-series', 'detail', id] as const,
}

// ─── useCreateBookingSeries ───────────────────────────────────────────────────

export function useCreateBookingSeries() {
  const queryClient = useQueryClient()

  return useMutation<CreateBookingSeriesResult, Error, CreateBookingSeriesParams>({
    mutationFn: async ({ instructor_id, recurrence, booking_type, notes }) => {
      const { data, error } = await supabase.rpc('create_booking_series', {
        p_instructor_id: instructor_id,
        p_recurrence:    recurrence,
        p_booking_type:  booking_type,
        p_notes:         notes ?? null,
      })

      if (error) throw new Error(error.message)
      return data as CreateBookingSeriesResult
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: seriesKeys.all })
      // Invalida bookings para refletir as novas aulas
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
    },
  })
}

// ─── useCancelBookingSeries ───────────────────────────────────────────────────

export function useCancelBookingSeries() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (seriesId: string) => {
      const { error } = await supabase.rpc('cancel_booking_series', {
        p_series_id: seriesId,
      })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: seriesKeys.all })
      queryClient.invalidateQueries({ queryKey: ['bookings'] })
    },
  })
}

// ─── useBookingSeries ─────────────────────────────────────────────────────────
// Se seriesId for passado, busca uma série específica.
// Caso contrário, lista todas as séries do usuário autenticado.

export function useBookingSeries(seriesId?: string) {
  const { user } = useAuth()

  return useQuery<BookingSeries | BookingSeries[] | null>({
    queryKey: seriesId ? seriesKeys.detail(seriesId) : [...seriesKeys.all, user?.id],
    enabled:  Boolean(user?.id),
    staleTime: 1000 * 60 * 2,
    queryFn: async () => {
      if (seriesId) {
        const { data, error } = await supabase
          .from('booking_series')
          .select('*')
          .eq('id', seriesId)
          .maybeSingle()

        if (error) throw new Error(error.message)
        return data as BookingSeries | null
      }

      // Lista: mostra séries onde o usuário é student ou instructor
      const { data, error } = await supabase
        .from('booking_series')
        .select('*')
        .eq('student_id', user!.id)
        .order('created_at', { ascending: false })

      if (error) throw new Error(error.message)
      return (data ?? []) as BookingSeries[]
    },
  })
}
